import React, {useState, useEffect, useRef} from 'react';
import {Link as RouterLink} from 'react-router';
import {
    Box,
    Button,
    Container,
    Typography,
    Link,
    Paper,
} from '@mui/material';
import {
    School as SchoolIcon,
} from '@mui/icons-material';
import {useLogto} from '@logto/react';
import MathScribbleImage from '../../assets/MathScribble.png';
import QuantinoAvatar from '../../assets/avatars/quantino_motivierend.png';

const Login = () => {
    // Logto authentication
    const {signIn} = useLogto();
    const baseUrl = window.location.origin;

    // Animation states
    const [showContent, setShowContent] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(true);

    // Check if animation should run based on localStorage
    const checkAnimationCooldown = () => {
        try {
            const lastAnimationTime = localStorage.getItem('duoquanto_last_login_animation');
            if (lastAnimationTime) {
                const timeDiff = Date.now() - parseInt(lastAnimationTime);
                const oneMinute = 60 * 1000; // 1 minute in milliseconds
                return timeDiff > oneMinute;
            }
            return true; // First time, should animate
        } catch (error) {
            console.warn('localStorage not available:', error);
            return true; // Fallback to always animate if localStorage fails
        }
    };

    // Set animation timestamp in localStorage
    const setAnimationTimestamp = () => {
        try {
            localStorage.setItem('duoquanto_last_login_animation', Date.now().toString());
        } catch (error) {
            console.warn('Could not save to localStorage:', error);
        }
    };

    // Handle login with Logto
    const handleLogin = () => {
        signIn(`${baseUrl}/callback`);
    };

    // Trigger content animation after background animation
    useEffect(() => {
        const shouldRunAnimation = checkAnimationCooldown();
        setShouldAnimate(shouldRunAnimation);

        if (shouldRunAnimation) {
            const timer = setTimeout(() => {
                setShowContent(true);
                setAnimationTimestamp(); // Record that animation played
            }, 400); // Delay to let background animation complete first

            return () => clearTimeout(timer);
        } else {
            // If within cooldown, show content immediately
            setShowContent(true);
        }
    }, []);

    return (
        <Box sx={{position: 'relative', overflow: 'hidden'}}>
            {/* Layer 1: Background Image (Blurred with zoom animation) */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${MathScribbleImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    filter: 'blur(8px)',
                    zIndex: -2,
                    // Background zoom animation - only play if should animate
                    animation: shouldAnimate ? 'backgroundZoom 1.2s ease-out forwards' : 'none',
                    transform: shouldAnimate ? 'scale(0.95)' : 'scale(1)',
                    opacity: shouldAnimate ? 0 : 1,
                    '@keyframes backgroundZoom': {
                        '0%': {
                            transform: 'scale(0.9)',
                            opacity: 0,
                        },
                        '100%': {
                            transform: 'scale(1)',
                            opacity: 1,
                        },
                    },
                }}
            />

            {/* Layer 2: Optional overlay for better contrast with fade in */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    zIndex: -1,
                    animation: shouldAnimate ? 'fadeIn 1s ease-out forwards' : 'none',
                    opacity: shouldAnimate ? 0 : 1,
                    '@keyframes fadeIn': {
                        '0%': {
                            opacity: 0,
                        },
                        '100%': {
                            opacity: 1,
                        },
                    },
                }}
            />

            {/* Layer 3: Combined Content Container with entrance animation */}
            <Container component="main" maxWidth="sm" sx={{
                py: 4,
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1,
            }}>
                <Paper
                    elevation={6}
                    sx={{
                        width: '100%',
                        maxWidth: 500,
                        p: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backdropFilter: 'blur(10px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        position: 'relative',
                        // Content entrance animation
                        opacity: showContent ? 1 : 0,
                        transform: showContent ? 'translateY(0)' : 'translateY(30px)',
                        transition: shouldAnimate ? 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                    }}
                >
                    {/* Branding Section */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        justifyContent: 'center',
                        // Staggered animation for branding
                        opacity: showContent ? 1 : 0,
                        transform: showContent ? 'translateY(0)' : 'translateY(20px)',
                        transition: shouldAnimate ? 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s' : 'none',
                    }}>
                        <SchoolIcon sx={{
                            mr: 2,
                            fontSize: 60,
                            color: 'primary.main',
                            // Icon rotation animation
                            transform: showContent ? 'rotate(0deg)' : 'rotate(-10deg)',
                            transition: shouldAnimate ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.3s' : 'none',
                        }}/>
                        <Typography variant="h3" component="h1" color="primary.main" fontWeight="bold">
                            DuoQuanto
                        </Typography>
                    </Box>

                    {/* Description Section */}
                    <Box sx={{
                        textAlign: 'center',
                        mb: 4,
                        // Staggered animation for description
                        opacity: showContent ? 1 : 0,
                        transform: showContent ? 'translateY(0)' : 'translateY(20px)',
                        transition: shouldAnimate ? 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.4s' : 'none',
                    }}>
                        <Typography variant="h5" color="primary" gutterBottom fontWeight="medium">
                            Gamified Learning <br/> for Information Systems
                        </Typography>
                        <Typography variant="body1" paragraph color="text.secondary" sx={{mt: 2}}>
                            Welcome to your interactive learning platform for mathematics and statistics.
                        </Typography>
                    </Box>

                    {/* Login Section */}
                    <Box sx={{
                        width: '100%',
                        textAlign: 'center',
                        pb: 9,
                        // Staggered animation for login button
                        opacity: showContent ? 1 : 0,
                        transform: showContent ? 'translateY(0)' : 'translateY(20px)',
                        transition: shouldAnimate ? 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.6s' : 'none',
                    }}>
                        <Button
                            onClick={handleLogin}
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{
                                mt: 2,
                                mb: 3,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'medium',
                                // Button hover animation
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4,
                                },
                            }}
                        >
                            Sign In to Your Account
                        </Button>
                    </Box>

                    {/* Avatar positioned at bottom right with bounce animation */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -10,
                            right: -0,
                            width: 120,
                            height: 120,
                            backgroundImage: `url(${QuantinoAvatar})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center bottom',
                            zIndex: 2,
                            pointerEvents: 'none',
                            // Avatar entrance animation
                            opacity: showContent ? 1 : 0,
                            transform: showContent ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)',
                            transition: shouldAnimate ? 'all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.8s' : 'none',
                        }}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;